import React, {useEffect, useMemo, useState} from 'react';
import Forms from './Forms';

const FALLBACK_ASPECT_RATIO = 4 / 3;

function PhotoGallery({
    title,
    items,
    emptyMessage,
    removeLabel = 'Smazat',
    onRemove,
    disabled = false
}) {
    const safeItems = useMemo(
        () => (Array.isArray(items) ? items.filter(Boolean) : []),
        [items]
    );
    const [activeIndex, setActiveIndex] = useState(null);

    useEffect(() => {
        if (!safeItems.length) {
            setActiveIndex(null);
            return;
        }
        if (activeIndex === null) {
            return;
        }
        if (activeIndex >= safeItems.length) {
            setActiveIndex(safeItems.length - 1);
        }
    }, [activeIndex, safeItems]);

    useEffect(() => {
        if (activeIndex === null) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                setActiveIndex(null);
                return;
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                setActiveIndex((prev) => {
                    if (prev === null || !safeItems.length) return null;
                    return (prev - 1 + safeItems.length) % safeItems.length;
                });
                return;
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                setActiveIndex((prev) => {
                    if (prev === null || !safeItems.length) return null;
                    return (prev + 1) % safeItems.length;
                });
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [activeIndex, safeItems.length]);

    const activeItem = activeIndex !== null ? safeItems[activeIndex] : null;
    const canNavigate = safeItems.length > 1;

    const openAtIndex = (index) => {
        if (index < 0 || index >= safeItems.length) {
            return;
        }
        setActiveIndex(index);
    };

    const goPrev = () => {
        if (!safeItems.length) return;
        setActiveIndex((prev) => {
            if (prev === null) return 0;
            return (prev - 1 + safeItems.length) % safeItems.length;
        });
    };

    const goNext = () => {
        if (!safeItems.length) return;
        setActiveIndex((prev) => {
            if (prev === null) return 0;
            return (prev + 1) % safeItems.length;
        });
    };

    return (
        <div className="rounded border p-3">
            {title && (
                <div className="text-sm font-medium mb-2">{title}</div>
            )}
            {!safeItems.length && (
                <div className="text-sm opacity-70">{emptyMessage}</div>
            )}
            {!!safeItems.length && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {safeItems.map((item, index) => {
                        const aspectRatio = resolveAspectRatio(item);
                        return (
                            <div key={item.id || `${item.previewUrl || item.fullUrl || item.name}_${index}`} className="border rounded p-2">
                                <button
                                    type="button"
                                    className="block w-full rounded overflow-hidden bg-gray-100"
                                    style={{aspectRatio}}
                                    onClick={() => openAtIndex(index)}
                                    disabled={!item.fullUrl}
                                    title="Otevřít fotku"
                                >
                                    {item.previewUrl ? (
                                        <img
                                            src={item.previewUrl}
                                            alt={item.alt || item.name || `Fotka ${index + 1}`}
                                            className="w-full h-full object-contain bg-gray-100"
                                        />
                                    ) : (
                                        <div className="w-full h-full min-h-24 bg-gray-100 flex items-center justify-center text-xs opacity-70">
                                            Bez náhledu
                                        </div>
                                    )}
                                </button>
                                <div className="mt-2 space-y-2">
                                    {(item.name || item.meta) && (
                                        <div className="text-xs leading-tight break-all">
                                            <div className="font-medium">{item.name || '-'}</div>
                                            {item.meta && <div className="opacity-70">{item.meta}</div>}
                                        </div>
                                    )}
                                    {onRemove && (
                                        <Forms.Button
                                            label={removeLabel}
                                            className="bg-red-600 hover:bg-red-700 w-full"
                                            onClick={() => onRemove(item, index)}
                                            disabled={disabled}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {activeItem && (
                <div
                    className="fixed inset-0 z-50 bg-black/90 flex flex-col"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setActiveIndex(null)}
                >
                    <div className="flex items-center justify-between text-white px-4 py-3">
                        <div className="text-sm opacity-90 truncate pr-3">
                            {activeItem.name || 'Náhled fotografie'}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs opacity-80">{activeIndex + 1} / {safeItems.length}</span>
                            <button
                                type="button"
                                className="w-10 h-10 rounded border border-white/40 hover:bg-white/10"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setActiveIndex(null);
                                }}
                                aria-label="Zavřít galerii"
                            >
                                ×
                            </button>
                        </div>
                    </div>

                    <div className="relative flex-1 flex items-center justify-center px-4 pb-4" onClick={(event) => event.stopPropagation()}>
                        {canNavigate && (
                            <button
                                type="button"
                                className="absolute left-4 z-10 w-11 h-11 rounded-full bg-black/60 border border-white/40 text-white text-2xl leading-none hover:bg-black/80"
                                onClick={goPrev}
                                aria-label="Předchozí fotka"
                            >
                                ‹
                            </button>
                        )}
                        {activeItem.fullUrl ? (
                            <img
                                src={activeItem.fullUrl}
                                alt={activeItem.alt || activeItem.name || 'Fotka'}
                                className="max-w-[92vw] max-h-[80vh] object-contain rounded"
                            />
                        ) : (
                            <div className="text-white/80 text-sm">Nelze zobrazit plný náhled.</div>
                        )}
                        {canNavigate && (
                            <button
                                type="button"
                                className="absolute right-4 z-10 w-11 h-11 rounded-full bg-black/60 border border-white/40 text-white text-2xl leading-none hover:bg-black/80"
                                onClick={goNext}
                                aria-label="Další fotka"
                            >
                                ›
                            </button>
                        )}
                    </div>

                    {canNavigate && (
                        <div className="px-4 pb-4">
                            <div className="mx-auto max-w-5xl flex gap-2 overflow-x-auto">
                                {safeItems.map((item, index) => {
                                    const isActive = index === activeIndex;
                                    const itemAspectRatio = resolveAspectRatio(item);
                                    return (
                                        <button
                                            key={`thumb_${item.id || item.previewUrl || item.name}_${index}`}
                                            type="button"
                                            className={`shrink-0 rounded overflow-hidden border-2 ${isActive ? 'border-white' : 'border-transparent'}`}
                                            style={{width: 88, aspectRatio: itemAspectRatio}}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                openAtIndex(index);
                                            }}
                                        >
                                            {item.previewUrl ? (
                                                <img src={item.previewUrl} alt={item.alt || `Náhled ${index + 1}`} className="w-full h-full object-cover"/>
                                            ) : (
                                                <div className="w-full h-full bg-gray-600"/>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function resolveAspectRatio(item) {
    const width = Number(item?.width);
    const height = Number(item?.height);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return FALLBACK_ASPECT_RATIO;
    }
    return width / height;
}

export default PhotoGallery;
